import type { RRRouteObject } from "src/router";
import type { SpacesAccess } from "./types";
import type { RouteSpacesAccessHandle } from "./prepare-routes";

type PathAccessRegistry = Map<string, SpacesAccess>;

let _pathAccessRegistry: PathAccessRegistry | null = null;

function buildPathRegistry(
  routes: RRRouteObject[],
  registry: PathAccessRegistry = new Map(),
  basePath = "",
): PathAccessRegistry {
  for (const route of routes) {
    const handle = route.handle as RouteSpacesAccessHandle | undefined;
    const spacesAccess = handle?.spacesAccess;

    if (route.path) {
      // Strip catch-all wildcards (e.g., "templates/*" -> "templates")
      const cleanPath = route.path.replace(/\/?\*$/, "");
      const fullPath = basePath ? `${basePath}/${cleanPath}` : `/${cleanPath}`;
      if (spacesAccess !== undefined) {
        registry.set(fullPath, spacesAccess);
      }

      // Recursively process children
      if (!route.index && route.children) {
        buildPathRegistry(route.children, registry, fullPath);
      }
    } else if (route.index) {
      // Index routes use the base path
      if (basePath && spacesAccess !== undefined) {
        registry.set(basePath, spacesAccess);
      }
    }
  }
  return registry;
}

/**
 * Called by json-slug-routes.tsx after building the route tree.
 * This reverses the dependency direction: the route module pushes
 * routes into the registry instead of the registry pulling them.
 */
export function setRoutes(routes: RRRouteObject[]): void {
  _pathAccessRegistry = buildPathRegistry(routes);
}

function getPathAccessRegistry(): PathAccessRegistry {
  if (!_pathAccessRegistry) {
    throw new Error(
      "Path access registry not initialized. Ensure json-slug-routes.tsx calls setRoutes() at module load time.",
    );
  }
  return _pathAccessRegistry;
}

export function isPathAccessibleInSpace(path: string): boolean {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const access = getPathAccessRegistry().get(normalizedPath);
  // If not found in registry, default to hidden (only explicitly declared routes are accessible)
  if (!access) {
    return false;
  }
  return access !== "hidden-from-limited-view";
}

/**
 * Check if a path or any of its child paths are accessible when in a space.
 * Useful for nav sections that should show if any child route is accessible.
 */
export function isPathOrChildrenAccessibleInSpace(path: string): boolean {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (isPathAccessibleInSpace(normalizedPath)) {
    return true;
  }

  for (const [registryPath, access] of getPathAccessRegistry().entries()) {
    if (
      registryPath.startsWith(normalizedPath) &&
      registryPath !== normalizedPath &&
      access !== "hidden-from-limited-view"
    ) {
      return true;
    }
  }

  return false;
}
